import 'element-closest';
import gsap from 'gsap';
import { ANIMATIONS } from '../config';

class Accordion {

  constructor(options = {}) {
    this.$root = options.element;
    this.$headers = this.$root.querySelectorAll('.accordion-entry__header');
    this.$entries = this.$root.querySelectorAll('.accordion-entry');
    this.isInitialized = false;
  }

  // noinspection JSUnusedGlobalSymbols: used via reflection
  init() {
    if (!this.isInitialized) {
      this.isInitialized = true;
      this.bindEvents();

      Array.prototype.slice.call(this.$entries).forEach(($entry) => {
        if ($entry.classList.contains('accordion-entry--open')) {
          const $currentContent = $entry.querySelector('.accordion-entry__header').nextElementSibling;
          const $currentIconPlus = $entry.querySelector('.accordion-entry__icon-plus');
          const $currentIconMinus = $entry.querySelector('.accordion-entry__icon-minus');
          const applyInitialOffset = this.$entries.length > 1;
          Accordion.openContentInitially(
            $currentContent,
            $currentIconPlus,
            $currentIconMinus,
            applyInitialOffset,
          );
        } else {
          // #fixYoutubePreviewInAccordion (AMSBFVB-6)
          const videos = $entry.querySelectorAll('.videoplayer iframe');
          videos.forEach(($video) => {
            Accordion.isYouTubeIframe($video) &&
            $video.classList.add('accordion-entry__youtube-video-iframe'); // if there is youtube video, add class to be able to target it for reload on open
          });

        }
      });
    }
  }

  /**
   * bind onClick event to each accordion entry header
   */
  bindEvents() {

    Array.prototype.slice.call(this.$headers).forEach(($header) => {
      const $head = $header;
      if ($head.dataset.hasClickListener) {
        // A parent already has setup listener, don't do it again.
        return;
      }

      $head.addEventListener('click', (event) => {
        event.stopPropagation();
        event.preventDefault(); // don't scroll to beginning of site

        const $currentEntry = event.target.closest('.accordion-entry');
        const $currentContent = event.target.closest('.accordion-entry__header').nextElementSibling;
        const $currentIconPlus = $currentEntry.querySelector('.accordion-entry__icon-plus');
        const $currentIconMinus = $currentEntry.querySelector('.accordion-entry__icon-minus');

        $currentContent.classList.add('accordion-entry__content--animating');

        if ($currentEntry.classList.contains('accordion-entry--open')) {
          Accordion.closeContent($currentContent, $currentIconPlus, $currentIconMinus, () => {
            $currentEntry.classList.toggle('accordion-entry--open');
            $currentContent.classList.remove('accordion-entry__content--animating');
          });
        } else {
          Accordion.reloadVideoIframesMaybe($currentContent);  // #fixYoutubePreviewInAccordion (AMSBFVB-6)
          
          $currentEntry.classList.toggle('accordion-entry--open');     
          Accordion.openContent($currentContent, $currentIconPlus, $currentIconMinus, () => {
            $currentContent.classList.remove('accordion-entry__content--animating');
          });
        }
      }, true);

      $head.dataset.hasClickListener = true;
    });
  }

  /**
   * Animate "+" to "-"
   * Animate height of content expanding
   * @param $content
   * @param $iconPlus
   * @param $iconMinus
   * @param animationCallback
   */
  static openContent($content, $iconPlus, $iconMinus, animationCallback) {
    gsap.to($iconPlus, ANIMATIONS.default.DURATION, {
      ease: ANIMATIONS.default.CURVE,
      scaleX: 0,
    });

    gsap.to($iconMinus, ANIMATIONS.default.DURATION, {
      ease: ANIMATIONS.default.CURVE,
      scaleX: 0.125,
    });

    gsap.to($iconMinus, ANIMATIONS.default.DURATION, {
      delay: ANIMATIONS.default.DURATION,
      ease: ANIMATIONS.default.CURVE,
      scaleX: 1,
    });

    gsap.set($content, {
      height: 'auto',
    });

    gsap.from($content, ANIMATIONS.default.DURATION * 2, {
      ease: ANIMATIONS.default.CURVE,
      height: 0,
      onComplete: () => {
        gsap.set($content, {
          height: 'auto',
        });
        animationCallback();
      },
    });
  }

  /**
   * Set "+" to "-"
   * Set height to height of expanded content
   * @param $content
   * @param $iconPlus
   * @param $iconMinus
   */
  static openContentInitially($content, $iconPlus, $iconMinus, applyOffset = true) {
    const offset = applyOffset ? -8 : 0;

    gsap.set($iconPlus, {
      scaleX: 0,
      x: offset,
      y: offset,
    });

    gsap.set($iconMinus, {
      scaleX: 0.125,
      x: offset,
      y: offset,
    });

    gsap.set($iconMinus, {
      scaleX: 1,
      x: offset,
      y: offset,
    });

    gsap.set($content, {
      height: 'auto',
    });
  }

  /**
   * Animate "-" to "+"
   * Animate height of content collapsing
   * @param $content
   * @param $iconPlus
   * @param $iconMinus
   * @param animationCallback
   */
  static closeContent($content, $iconPlus, $iconMinus, animationCallback) {
    gsap.to($iconPlus, ANIMATIONS.default.DURATION, {
      ease: ANIMATIONS.default.CURVE,
      scaleX: 0.125,
    });

    gsap.to($iconPlus, ANIMATIONS.default.DURATION, {
      delay: ANIMATIONS.default.DURATION,
      ease: ANIMATIONS.default.CURVE,
      scaleX: 1,
    });

    gsap.to($iconMinus, ANIMATIONS.default.DURATION, {
      ease: ANIMATIONS.default.CURVE,
      scaleX: 0.125,
    });

    gsap.to($iconMinus, ANIMATIONS.default.DURATION, {
      delay: ANIMATIONS.default.DURATION,
      ease: ANIMATIONS.default.CURVE,
      scaleX: 1,
    });

    gsap.to($content, ANIMATIONS.default.DURATION * 2, {
      ease: ANIMATIONS.default.CURVE,
      height: 0,
      onComplete: animationCallback,
    });

    Accordion.pauseVideos($content);
  }

  /**
   * Pauses all Youtube videos inside of $content
   * @param $content
   */
  static pauseVideos($content) {
    const $videos = $content.querySelectorAll('.videoplayer iframe');
    Array.prototype.slice.apply($videos).forEach(($video) => {
      $video.contentWindow.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo', args: '' }), '*'); // pause youtube video
    });
  }

  // #fixYoutubePreviewInAccordion (AMSBFVB-6)
  /**
   * Fix for YouTube iframe preview issue inside collapsed accordion.
   *
   * When the accordion is closed (hidden), YouTube renders a low-quality preview
   * (default.webp), because it cannot determine the correct iframe size.
   *
   * On accordion open we force iframe reload to trigger proper rendering
   * with correct dimensions.
   *
   * Applied only once per iframe (using `is-ready` flag) to avoid unnecessary reloads.
   */
  static reloadVideoIframesMaybe($content) {
    const $videos = $content.querySelectorAll('.accordion-entry__youtube-video-iframe');
    $videos.forEach(($video) => {
      if ($video.classList.contains('is-ready')) return;

      $video.onload = () => {
        $video.classList.add('is-ready');
      };

      $video.src = $video.src;
    });
  }

   // #fixYoutubePreviewInAccordion (AMSBFVB-6)
  static isYouTubeIframe(iframe) {
    try {
      const url = new URL(iframe.src);
      return [
        'www.youtube.com',
        'youtube.com',
        'm.youtube.com',
        'www.youtube-nocookie.com'
      ].includes(url.hostname);
    } catch (e) {
      return false;
    }
  }
}

export default Accordion;
